<script setup lang="ts">
import { computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import { ApiClientError } from "../api/client";
import { getProduct, type ProductDetail } from "../api/products";
import { listServiceCandidates, saveProductServiceInfo, serviceFields,
  type ServiceValues, type ServiceConfig, type ServiceKey, type ServiceCandidate } from "../api/product-service-info";

import { coreServiceFields } from "./service-info-defaults";
import ServiceInfoTemplateTools from "./ServiceInfoTemplateTools.vue";
import type { ServiceSnapshot } from "../api/service-info-templates";

const props = defineProps<{ product: ProductDetail; material: string; disabled: boolean }>();
const emit = defineEmits<{ saved: [product: ProductDetail] }>();
const enabled = computed(() => Boolean(props.product.serviceInfo));
const values = reactive<ServiceValues>({ shippingLeadTime: null, shippingDescription: null, freeShippingDescription: null,
  guaranteeDescription: null, invoiceDescription: null, craftsmanshipDescription: null });
const config = reactive<ServiceConfig>({ standard: {} as ServiceConfig["standard"], materialText: null, custom: [] });
const baseline = ref("");
const version = ref(0);
const baselineUpdatedAt = ref("");
const busy = ref(false);
const blocked = ref(false);
const message = ref("");
const candidates = ref<ServiceCandidate[]>([]);
const selectedCandidates = ref<string[]>([]);
const summaryMax = 120;
const expanded = reactive<Record<string, boolean>>({});
const coreKeys = new Set<ServiceKey>(coreServiceFields.map((field) => field.key));
function toggleDetails(id: string) { expanded[id] = !expanded[id]; }
function applySnapshot(snapshot: ServiceSnapshot) {
  if (props.disabled || busy.value || blocked.value) return;
  Object.assign(values, snapshot.values);
  config.standard = snapshot.config.standard; config.materialText = snapshot.config.materialText; config.custom = snapshot.config.custom;
  for (const id of Object.keys(expanded)) delete expanded[id];
  message.value = "已应用模板到当前草稿，点击保存服务信息后生效。";
}
function applyGeneralTemplate() {
  if (props.disabled || busy.value || blocked.value) return;
  for (const field of coreServiceFields) {
    values[field.key] = field.content;
    config.standard[field.key].visible = true;
    config.standard[field.key].title = field.title;
  }
  for (const field of serviceFields) if (!coreKeys.has(field.key)) config.standard[field.key].visible = false;
  message.value = "已填入四项通用详情并勾选，点击保存服务信息后生效。";
}
function snapshot() { return JSON.stringify({ values, config }); }
const dirty = computed(() => enabled.value && snapshot() !== baseline.value);
function sync(product: ProductDetail) {
  for (const { key } of serviceFields) if (key !== "material") values[key] = product[key] ?? null;
  for (const id of Object.keys(expanded)) delete expanded[id];
  const saved = product.serviceInfo?.config;
  config.standard = Object.fromEntries(serviceFields.map(({ key }, index) => {
    const savedItem = saved?.standard[key];
    return [key, savedItem
      ? { ...savedItem, title: savedItem.title ?? null, summary: savedItem.summary ?? null }
      : { visible: coreKeys.has(key), sortOrder: index * 10, title: coreServiceFields.find((field) => field.key === key)?.title ?? null, summary: null }];
  })) as ServiceConfig["standard"];
  config.materialText = saved ? saved.materialText : null;
  config.custom = (saved?.custom ?? []).map((item) => ({ ...item, summary: item.summary ?? null }));
  version.value = product.serviceInfo?.version ?? 0;
  baselineUpdatedAt.value = product.updatedAt;
  baseline.value = snapshot(); blocked.value = false; message.value = "";
}
watch(() => props.product, (product, previous) => {
  if (!previous || product.id !== previous.id || !dirty.value) sync(product);
}, { immediate: true });
function title(key: ServiceKey, fallback: string) {
  const coreTitle = coreServiceFields.find((field) => field.key === key)?.title;
  if (coreTitle) return coreTitle;
  return !props.product.customizationEnabled ? ({ shippingLeadTime: "发货时效", shippingDescription: "发货说明", freeShippingDescription: "运费说明" } as Partial<Record<ServiceKey, string>>)[key] ?? fallback : fallback;
}
function effectiveTitle(key: ServiceKey, fallback: string) {
  return config.standard[key]?.title?.trim() || title(key, fallback);
}
function normalizedTitle(value: string) {
  return value.normalize("NFKC").trim().toLowerCase();
}
function fixedTitleCandidates() {
  const stockTitles: Partial<Record<ServiceKey, string>> = {
    shippingLeadTime: "发货时效", shippingDescription: "发货说明", freeShippingDescription: "运费说明",
  };
  return serviceFields.flatMap(({ key, title: fallback }) => {
    const override = config.standard[key]?.title?.trim();
    const candidates = override ? [override] : [fallback, ...(stockTitles[key] ? [stockTitles[key]!] : [])];
    return candidates.map(normalizedTitle);
  });
}
function reservedTitleNames() {
  return new Set([
    ...fixedTitleCandidates(),
    ...serviceFields.map((item) => normalizedTitle(item.title)),
    "发货时效", "发货说明", "运费说明", "售后保障", "定制须知",
  ].map(normalizedTitle));
}
function restoreTitle(key: ServiceKey) {
  config.standard[key].title = coreServiceFields.find((field) => field.key === key)?.title ?? null;
}
function addItem() {
  if (props.disabled || busy.value || blocked.value) return;
  if (config.custom.length >= 20) { message.value = "每商品最多添加20项服务说明"; return; }
  config.custom.push({ id: crypto.randomUUID(), title: "", summary: "", content: "", visible: true,
    sortOrder: Math.min(100000, Math.max(...Object.values(config.standard).map((item) => item.sortOrder), ...config.custom.map((item) => item.sortOrder)) + 10) });
}
async function loadCandidates() {
  try { candidates.value = await listServiceCandidates(); }
  catch (error) { message.value = error instanceof Error ? error.message : "候选读取失败"; }
}
function copyCandidates() {
  const chosen = candidates.value.filter((item) => selectedCandidates.value.includes(item.id));
  const names = reservedTitleNames();
  config.custom.forEach((item) => names.add(normalizedTitle(item.title)));
  if (chosen.some((item) => names.has(normalizedTitle(item.title))) || config.custom.length + chosen.length > 20) {
    message.value = "候选标题重复或超过20项，请调整选择"; return;
  }
  for (const item of chosen) {
    addItem(); Object.assign(config.custom[config.custom.length - 1]!, { title: item.title, content: item.content,
      summary: item.summary ?? item.content });
  }
  selectedCandidates.value = [];
}
type ServiceOrderRef = { id: string; sortOrder: number; set: (value: number) => void };
function serviceOrderRefs(): ServiceOrderRef[] {
  return [
    ...serviceFields.map(({ key }) => ({ id: key, sortOrder: config.standard[key].sortOrder,
      set: (value: number) => { config.standard[key].sortOrder = value; } })),
    ...config.custom.map((item) => ({ id: `custom:${item.id}`, sortOrder: item.sortOrder,
      set: (value: number) => { item.sortOrder = value; } })),
  ].sort((left, right) => left.sortOrder - right.sortOrder || left.id.localeCompare(right.id));
}
function canMove(id: string, direction: -1 | 1): boolean {
  const index = serviceOrderRefs().findIndex((item) => item.id === id);
  return index >= 0 && index + direction >= 0 && index + direction < serviceOrderRefs().length;
}
function moveService(id: string, direction: -1 | 1): void {
  const ordered = serviceOrderRefs();
  const index = ordered.findIndex((item) => item.id === id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= ordered.length) return;
  [ordered[index], ordered[target]] = [ordered[target]!, ordered[index]!];
  ordered.forEach((item, position) => item.set(position * 10));
}
function canLeave(): boolean {
  if (busy.value) { message.value = "服务信息正在保存，请等待结果"; return false; }
  if (blocked.value) { message.value = "保存结果需要核对，请先点击核对最新服务信息"; return false; }
  return !dirty.value || window.confirm("服务信息有未保存修改，是否放弃草稿并离开？");
}
async function reconcile() {
  if (busy.value || !window.confirm("将读取最新服务信息并放弃当前草稿，是否继续？")) return;
  busy.value = true;
  try { const product = await getProduct(props.product.id); sync(product); emit("saved", product); }
  catch (error) { message.value = error instanceof Error ? error.message : "读取失败，请保留草稿"; }
  finally { busy.value = false; }
}
async function save(product: ProductDetail): Promise<ProductDetail> {
  if (!dirty.value) return product;
  if (blocked.value || busy.value) throw new Error("请先核对最新服务信息，当前不允许重复保存");
  if (product.id !== props.product.id || product.updatedAt !== baselineUpdatedAt.value) {
    blocked.value = true; message.value = "商品版本已变化，草稿已保留，请先核对最新服务信息";
    throw new Error(message.value);
  }
  // The API retains legacy default names; persist the new defaults explicitly.
  for (const field of coreServiceFields) {
    if (!config.standard[field.key].title?.trim()) config.standard[field.key].title = field.title;
  }
  const names = config.custom.map((item) => normalizedTitle(item.title));
  const fixedNames = fixedTitleCandidates();
  const reserved = reservedTitleNames();
  if (new Set(fixedNames).size !== fixedNames.length || names.some((name) => !name || reserved.has(name)) || new Set(names).size !== names.length) {
    throw new Error("请填写非空且不重复的服务项标题");
  }
  const summaries = [...Object.values(config.standard), ...config.custom].map((item) => item.summary?.trim() ?? "");
  if (summaries.some((summary) => summary.length > summaryMax)) throw new Error(`列表简短说明不能超过${summaryMax}个字符`);
  busy.value = true; message.value = "";
  try {
    const result = await saveProductServiceInfo(product, { ...values }, JSON.parse(JSON.stringify(config)) as ServiceConfig, version.value);
    sync(result); emit("saved", result); return result;
  } catch (error) {
    blocked.value = !(error instanceof ApiClientError) || ![400, 422].includes(error.status);
    message.value = `${error instanceof Error ? error.message : "保存结果未知"}；草稿已保留，请先核对最新服务信息，勿重复提交。`;
    throw new Error(message.value);
  } finally { busy.value = false; }
}
function discardNewItem(id: string) {
  if (props.disabled || busy.value || blocked.value) return;
  config.custom = config.custom.filter((item) => item.id !== id);
  delete expanded[`custom:${id}`];
}
function cancelDraft() {
  if (!busy.value && !blocked.value && (!dirty.value || window.confirm("放弃未保存的服务修改？"))) sync(props.product);
}
function beforeUnload(event: BeforeUnloadEvent) {
  if (dirty.value || busy.value || blocked.value) { event.preventDefault(); event.returnValue = ""; }
}
if (typeof window !== "undefined") window.addEventListener("beforeunload", beforeUnload);
onBeforeUnmount(() => { if (typeof window !== "undefined") window.removeEventListener("beforeunload", beforeUnload); });
defineExpose({ enabled, dirty, busy, canLeave, save });
</script>

<template>
  <div
    v-if="enabled"
    class="service-editor"
  >
    <fieldset :disabled="disabled || busy || blocked">
      <legend class="sr-only">
        当前商品服务信息
      </legend>
      <div class="service-toolbar">
        <button
          type="button"
          class="service-template"
          @click="applyGeneralTemplate"
        >
          一键应用通用模板
        </button>
        <span>填入四项通用详情，保存后生效</span>
      </div>
      <ServiceInfoTemplateTools
        :snapshot="{ values, config }"
        :disabled="disabled || busy || blocked"
        @apply="applySnapshot"
      />
      <div class="service-list">
        <article
          v-for="field in coreServiceFields"
          :key="field.key"
          class="service-row"
          :style="{ order: config.standard[field.key].sortOrder }"
        >
          <div class="service-row__line">
            <input
              v-model="config.standard[field.key].visible"
              type="checkbox"
              :aria-label="`显示${effectiveTitle(field.key, field.title)}`"
            >
            <strong>{{ effectiveTitle(field.key, field.title) }}</strong>
            <input
              v-model="config.standard[field.key].summary"
              class="service-summary"
              :maxlength="summaryMax"
              :aria-label="`${field.title}简短说明`"
              placeholder="简短的一句话说明"
            >
            <button
              class="service-edit"
              type="button"
              :aria-expanded="Boolean(expanded[field.key])"
              :aria-controls="`service-detail-${field.key}`"
              @click="toggleDetails(field.key)"
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              ><path d="m15 5 4 4M4 20l4-1L20 7a2.8 2.8 0 0 0-4-4L4 15z" /></svg>
              {{ expanded[field.key] ? "收起详情" : "编辑详情" }}
            </button>
          </div>
          <div
            v-if="expanded[field.key]"
            :id="`service-detail-${field.key}`"
            class="service-row__detail"
          >
            <label class="content"><span>点击后的完整服务详情说明</span><textarea
              v-model="values[field.key]"
              :aria-label="`${field.title}详情说明`"
              :maxlength="field.max"
              rows="4"
            /></label>
            <details class="service-advanced">
              <summary>更多设置</summary>
              <label class="content"><span>服务项标题</span><input
                v-model="config.standard[field.key].title"
                maxlength="50"
                :placeholder="title(field.key, field.title)"
              ></label>
              <div class="service-order">
                <button
                  type="button"
                  @click="restoreTitle(field.key)"
                >
                  恢复默认
                </button>
                <button
                  type="button"
                  :disabled="!canMove(field.key, -1)"
                  :aria-label="`上移${field.title}`"
                  @click="moveService(field.key, -1)"
                >
                  上移
                </button>
                <button
                  type="button"
                  :disabled="!canMove(field.key, 1)"
                  :aria-label="`下移${field.title}`"
                  @click="moveService(field.key, 1)"
                >
                  下移
                </button>
              </div>
            </details>
          </div>
        </article>
        <article
          v-for="item in config.custom"
          :key="item.id"
          class="service-row service-row--custom"
          :style="{ order: item.sortOrder }"
        >
          <div class="service-row__line">
            <input
              v-model="item.visible"
              type="checkbox"
              :aria-label="`显示${item.title || '自定义服务项'}`"
            >
            <input
              v-model="item.title"
              maxlength="50"
              required
              aria-label="自定义服务名称"
              placeholder="服务名称"
            >
            <input
              v-model="item.summary"
              class="service-summary"
              :maxlength="summaryMax"
              aria-label="自定义服务简短说明"
              placeholder="简短的一句话说明"
            >
            <button
              class="service-edit"
              type="button"
              :aria-expanded="Boolean(expanded[`custom:${item.id}`])"
              :aria-controls="`service-detail-${item.id}`"
              @click="toggleDetails(`custom:${item.id}`)"
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              ><path d="m15 5 4 4M4 20l4-1L20 7a2.8 2.8 0 0 0-4-4L4 15z" /></svg>
              {{ expanded[`custom:${item.id}`] ? "收起详情" : "编辑详情" }}
            </button>
            <button
              class="service-delete"
              type="button"
              :aria-label="`删除${item.title || '自定义服务项'}`"
              @click="discardNewItem(item.id)"
            >
              <svg
                viewBox="0 0 24 24"
                aria-hidden="true"
              ><path d="M3 6h18M9 6V3h6v3M5 6l1 15h12l1-15M10 10v7m4-7v7" /></svg><span class="sr-only">删除</span>
            </button>
          </div>
          <div
            v-if="expanded[`custom:${item.id}`]"
            :id="`service-detail-${item.id}`"
            class="service-row__detail"
          >
            <label class="content"><span>点击后的完整服务详情说明</span><textarea
              v-model="item.content"
              maxlength="2000"
              aria-label="自定义服务详情说明"
              rows="4"
            /></label>
            <div class="service-order">
              <button
                type="button"
                :disabled="!canMove(`custom:${item.id}`, -1)"
                :aria-label="`上移${item.title}`"
                @click="moveService(`custom:${item.id}`, -1)"
              >
                上移
              </button>
              <button
                type="button"
                :disabled="!canMove(`custom:${item.id}`, 1)"
                :aria-label="`下移${item.title}`"
                @click="moveService(`custom:${item.id}`, 1)"
              >
                下移
              </button>
            </div>
          </div>
        </article>
      </div>
      <button
        class="service-add"
        type="button"
        :disabled="config.custom.length >= 20"
        @click="addItem"
      >
        + 添加自定义服务项
      </button>
      <details
        class="service-history"
        @toggle="loadCandidates"
      >
        <summary>从已保存服务项复制</summary>
        <label
          v-for="candidate in candidates"
          :key="candidate.id"
          class="visibility candidate"
        ><input
          v-model="selectedCandidates"
          type="checkbox"
          :value="candidate.id"
        >{{ candidate.title }}</label>
        <p v-if="!candidates.length">
          暂无候选，可直接添加服务项。
        </p>
        <button
          type="button"
          :disabled="!selectedCandidates.length"
          @click="copyCandidates"
        >
          复制所选服务项
        </button>
      </details>
    </fieldset>
    <div class="service-footer">
      <span>勾选控制展示；取消勾选保留原文。修改后请保存。</span>
      <button
        v-if="dirty && !blocked"
        type="button"
        :disabled="busy || disabled"
        @click="cancelDraft"
      >
        取消服务修改
      </button>
    </div>
    <p
      v-if="message"
      role="status"
      aria-live="polite"
    >
      {{ message }}
    </p>
    <button
      v-if="blocked"
      type="button"
      :disabled="busy || disabled"
      @click="reconcile"
    >
      核对最新服务信息
    </button>
  </div>
</template>

<style scoped>
.service-editor { margin-bottom: 24px; color: #171717; font-size: 13px; }
.service-editor p { margin: 12px 0; color: #666; line-height: 1.6; }
fieldset { min-width: 0; margin: 0; padding: 0; border: 0; }
.service-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; margin: 16px 0; }
.service-toolbar span, .service-footer { color: #666; font-size: 12px; }
button { border: 1px solid #d4d4d4; border-radius: 4px; padding: 8px 12px; background: #fff; color: #171717; font: inherit; cursor: pointer; }
button:hover:not(:disabled) { background: #f5f5f5; }
button:disabled, fieldset:disabled button { opacity: .5; cursor: default; }
button.service-template { border-color: #171717; background: #171717; color: #fff; font-weight: 600; }
button.service-template:hover:not(:disabled) { border-color: #333; background: #333; }
.service-list { display: flex; flex-direction: column; }
.service-row { border-top: 1px solid #e5e5e5; min-width: 0; }
.service-row:last-child { border-bottom: 1px solid #e5e5e5; }
.service-row__line { display: grid; grid-template-columns: 20px 140px minmax(0, 1fr) 96px; align-items: center; gap: 12px; min-height: 56px; padding: 8px 0; }
.service-row--custom .service-row__line { grid-template-columns: 20px 140px minmax(0, 1fr) 96px 32px; }
.service-row__line strong { min-width: 0; overflow-wrap: anywhere; font-size: 13px; font-weight: 500; }
input:not([type=checkbox]), textarea { width: 100%; min-width: 0; box-sizing: border-box; border: 1px solid #d4d4d4; border-radius: 4px; padding: 8px 10px; background: #fff; color: #171717; font: inherit; }
input::placeholder, textarea::placeholder { color: #737373; }
input[type=checkbox] { appearance: none; flex: none; width: 18px; height: 18px; margin: 0; border: 1px solid #ccc; border-radius: 3px; background: #fff; cursor: pointer; }
input[type=checkbox]:checked { border-color: #171717; background: #171717; }
input[type=checkbox]:checked::after { content: ''; display: block; width: 8px; height: 4px; margin: 4px 3px; border-left: 2px solid #fff; border-bottom: 2px solid #fff; transform: rotate(-45deg); }
input[type=checkbox]:disabled { cursor: default; opacity: .5; }
svg { width: 16px; height: 16px; fill: none; stroke: currentColor; stroke-width: 1.7; stroke-linecap: round; stroke-linejoin: round; flex: none; }
button.service-edit, button.service-delete { display: inline-flex; justify-content: center; align-items: center; gap: 6px; border: 0; padding: 8px 0; background: transparent; color: #444; white-space: nowrap; }
button.service-edit:hover:not(:disabled), button.service-delete:hover:not(:disabled) { color: #000; text-decoration: underline; background: transparent; }
.service-row__detail { display: grid; gap: 12px; padding: 4px 0 16px 32px; }
.content { display: grid; min-width: 0; gap: 8px; }
.content > span { font-size: 12px; color: #666; }
textarea { resize: vertical; line-height: 1.6; }
button.service-add { width: 100%; margin-top: 12px; border: 1px dashed #ccc; padding: 10px 12px; color: #171717; }
button.service-add:hover:not(:disabled) { border-color: #171717; background: #fafafa; }
.service-footer { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 12px; margin-top: 16px; }
.service-history { margin-top: 16px; color: #666; }
summary { cursor: pointer; padding: 4px 0; font-size: 12px; }
.visibility { display: flex; align-items: center; gap: 8px; }
.candidate { margin: 12px 0; }
.service-order { display: flex; gap: 8px; margin-top: 12px; }
:is(input, textarea, button, summary):focus-visible { outline: 2px solid #171717; outline-offset: 2px; }
.sr-only { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
@media (max-width: 640px) {
  .service-row__line { grid-template-columns: 20px minmax(0, 1fr) 96px; gap: 8px; }
  .service-row--custom .service-row__line { grid-template-columns: 20px minmax(0, 1fr) 96px 32px; }
  .service-summary { grid-column: 2 / -1; grid-row: 2; }
  .service-row__detail { padding-left: 28px; }
}
@media (forced-colors: active) { input[type=checkbox] { appearance: auto; } input[type=checkbox]:checked::after { display: none; } }
</style>
